import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import AdminLayout from '@/pages/admin/components/AdminLayout';

// ---- Types
interface Category {
  id: string;
  name: string;
  sort_order: number;
  created_at: string;
}

interface HighlightPreset {
  id: string;
  icon: string;
  title: string;
  desc: string;
  sort_order: number;
}

interface FaqPreset {
  id: string;
  question: string;
  answer: string;
  sort_order: number;
}

// ---- Constants
const ICON_OPTIONS = [
  'ri-star-line', 'ri-stack-line', 'ri-leaf-line', 'ri-shield-check-line',
  'ri-footprint-line', 'ri-plant-line', 'ri-home-3-line', 'ri-arrow-up-line',
  'ri-scissors-line', 'ri-refresh-line', 'ri-water-flash-line', 'ri-drop-line',
  'ri-sofa-line', 'ri-tools-line', 'ri-shield-line', 'ri-gift-line',
  'ri-circle-line', 'ri-safe-line', 'ri-building-line', 'ri-gamepad-line',
  'ri-home-smile-line', 'ri-heart-line', 'ri-fire-line', 'ri-flashlight-line',
  'ri-award-line', 'ri-medal-line', 'ri-thumb-up-line', 'ri-sun-line',
  'ri-sparkling-line', 'ri-rainbow-line', 'ri-palette-line', 'ri-brush-line',
  'ri-truck-line', 'ri-global-line', 'ri-recycle-line', 'ri-seedling-line',
  'ri-box-3-line', 'ri-rocket-line', 'ri-lightbulb-line', 'ri-eye-line',
];

type TabKey = 'categories' | 'highlights' | 'faqs';

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'categories', label: '品类管理', icon: 'ri-price-tag-3-line' },
  { key: 'highlights', label: '亮点管理', icon: 'ri-star-line' },
  { key: 'faqs', label: '常见问题', icon: 'ri-question-line' },
];

export default function AdminDefinitionsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('categories');
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  return (
    <AdminLayout title="定义管理">
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#1a1a1a] text-white text-sm px-5 py-2.5 rounded-full shadow-lg">
          {toast}
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex items-center gap-1 p-1 bg-[#f0ece6] rounded-full w-fit mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-white text-[#1a1a1a] shadow-sm'
                : 'text-[#888] hover:text-[#555]'
            }`}
          >
            <i className={`${tab.icon} text-base`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'categories' && <CategoriesPanel showToast={showToast} />}
      {activeTab === 'highlights' && <HighlightsPanel showToast={showToast} />}
      {activeTab === 'faqs' && <FaqsPanel showToast={showToast} />}
    </AdminLayout>
  );
}

// ===================== Categories Panel =====================
function CategoriesPanel({ showToast }: { showToast: (msg: string) => void }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .order('sort_order', { ascending: true });
    if (!error && data) setCategories(data as Category[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) { showToast('请输入品类名称'); return; }
    if (categories.some((c) => c.name === name)) { showToast('该品类已存在'); return; }
    setSaving(true);
    const maxSort = categories.reduce((max, c) => Math.max(max, c.sort_order), 0);
    const { error } = await supabase.from('categories').insert({ name, sort_order: maxSort + 1 });
    setSaving(false);
    if (error) { showToast('添加失败'); return; }
    setNewName('');
    showToast('品类已添加');
    fetchCategories();
  };

  const handleSaveEdit = async (id: string) => {
    const name = editName.trim();
    if (!name) { showToast('名称不能为空'); return; }
    setSaving(true);
    const { error } = await supabase.from('categories').update({ name }).eq('id', id);
    setSaving(false);
    if (error) { showToast('修改失败'); return; }
    setEditingId(null);
    showToast('品类已更新');
    fetchCategories();
  };

  const handleDelete = async (cat: Category) => {
    const { data: used } = await supabase.from('products').select('id').eq('category', cat.name).limit(1);
    if (used && used.length > 0) { showToast('该品类下有商品，请先处理商品后再删除'); return; }
    setSaving(true);
    const { error } = await supabase.from('categories').delete().eq('id', cat.id);
    setSaving(false);
    if (error) { showToast('删除失败'); return; }
    showToast('品类已删除');
    fetchCategories();
  };

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-[#888] mb-5">管理商品分类品类。修改品类名称后已有商品不受影响。删除前请确保没有商品属于该品类。</p>
      <div className="flex gap-3 mb-6">
        <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }} placeholder="输入新品类名称" className="flex-1 px-4 py-2.5 rounded-lg border border-[#e8e4de] bg-white text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors" />
        <button onClick={handleAdd} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#C8714A] text-white text-sm font-semibold hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"><i className="ri-add-line"></i>添加品类</button>
      </div>
      <div className="bg-white rounded-xl border border-[#ede9e3] overflow-hidden">
        {loading ? (<div className="flex flex-col gap-3 p-4">{Array.from({ length: 4 }).map((_, i) => (<div key={i} className="h-12 bg-[#f7f5f2] rounded-lg animate-pulse" />))}</div>)
        : categories.length === 0 ? (<div className="flex flex-col items-center justify-center py-12 gap-3 text-[#aaa]"><div className="w-10 h-10 flex items-center justify-center bg-[#f7f5f2] rounded-full"><i className="ri-price-tag-3-line text-lg"></i></div><p className="text-sm">暂无品类，请添加</p></div>)
        : (<div>{categories.map((cat, idx) => (
          <div key={cat.id} className={`flex items-center gap-4 px-4 py-3 border-b border-[#f0ece6] last:border-0 ${idx % 2 === 0 ? '' : 'bg-[#fdfcfb]'}`}>
            <div className="w-5 h-5 flex items-center justify-center text-[#ccc] flex-shrink-0"><i className="ri-price-tag-3-line"></i></div>
            {editingId === cat.id ? (
              <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(cat.id); if (e.key === 'Escape') setEditingId(null); }} className="flex-1 px-3 py-1.5 rounded border border-[#C8714A] text-sm text-[#1a1a1a] focus:outline-none" autoFocus />
            ) : (<span className="flex-1 text-sm font-medium text-[#1a1a1a]">{cat.name}</span>)}
            <span className="text-xs text-[#bbb]">排序: {cat.sort_order}</span>
            <div className="flex items-center gap-1">
              {editingId === cat.id ? (<><button onClick={() => handleSaveEdit(cat.id)} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg text-[#C8714A] hover:bg-[#C8714A]/10 transition-colors cursor-pointer"><i className="ri-check-line text-sm"></i></button><button onClick={() => setEditingId(null)} className="w-8 h-8 flex items-center justify-center rounded-lg text-[#aaa] hover:bg-[#f7f5f2] transition-colors cursor-pointer"><i className="ri-close-line text-sm"></i></button></>)
              : (<><button onClick={() => { setEditingId(cat.id); setEditName(cat.name); }} className="w-8 h-8 flex items-center justify-center rounded-lg text-[#888] hover:bg-[#f0ece6] hover:text-[#C8714A] transition-colors cursor-pointer" title="编辑"><i className="ri-edit-line text-sm"></i></button><button onClick={() => handleDelete(cat)} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg text-[#888] hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer" title="删除"><i className="ri-delete-bin-line text-sm"></i></button></>)}
            </div>
          </div>
        ))}</div>)}
      </div>
    </div>
  );
}

// ===================== Highlights Panel =====================
function HighlightsPanel({ showToast }: { showToast: (msg: string) => void }) {
  const [presets, setPresets] = useState<HighlightPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  // Form state
  const [formIcon, setFormIcon] = useState('ri-star-line');
  const [formTitle, setFormTitle] = useState('');
  const [formDesc, setFormDesc] = useState('');

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from('highlight_presets').select('*').order('sort_order', { ascending: true });
    if (!error && data) setPresets(data as HighlightPreset[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchPresets(); }, [fetchPresets]);

  const resetForm = () => { setFormIcon('ri-star-line'); setFormTitle(''); setFormDesc(''); setEditingId(null); setAddOpen(false); };

  const handleAdd = async () => {
    if (!formTitle.trim()) { showToast('请输入亮点标题'); return; }
    setSaving(true);
    const maxSort = presets.reduce((max, p) => Math.max(max, p.sort_order), 0);
    const { error } = await supabase.from('highlight_presets').insert({ icon: formIcon, title: formTitle.trim(), desc: formDesc.trim(), sort_order: maxSort + 1 });
    setSaving(false);
    if (error) { showToast('添加失败'); return; }
    resetForm();
    showToast('亮点已添加');
    fetchPresets();
  };

  const handleStartEdit = (p: HighlightPreset) => {
    setEditingId(p.id); setFormIcon(p.icon); setFormTitle(p.title); setFormDesc(p.desc); setAddOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !formTitle.trim()) { showToast('请输入亮点标题'); return; }
    setSaving(true);
    const { error } = await supabase.from('highlight_presets').update({ icon: formIcon, title: formTitle.trim(), desc: formDesc.trim() }).eq('id', editingId);
    setSaving(false);
    if (error) { showToast('修改失败'); return; }
    resetForm();
    showToast('亮点已更新');
    fetchPresets();
  };

  const handleDelete = async (id: string) => {
    setSaving(true);
    const { error } = await supabase.from('highlight_presets').delete().eq('id', id);
    setSaving(false);
    if (error) { showToast('删除失败'); return; }
    showToast('亮点已删除');
    fetchPresets();
  };

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-[#888] mb-5">管理产品亮点预设，这些内容会出现在新建/编辑商品弹窗的「产品亮点」下拉框中。</p>

      <div className="mb-5">
        {!addOpen ? (
          <button onClick={() => { resetForm(); setAddOpen(true); }} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#C8714A] text-white text-sm font-semibold hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i>添加亮点
          </button>
        ) : (
          <div className="rounded-xl border border-[#ede9e3] bg-white p-4 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C8714A]/10 flex-shrink-0">
                <i className={`${formIcon} text-[#C8714A] text-base`}></i>
              </div>
              <select value={formIcon} onChange={(e) => setFormIcon(e.target.value)} className="w-32 px-1.5 py-2 rounded border border-[#e8e4de] text-xs text-[#555] bg-white cursor-pointer focus:outline-none focus:border-[#C8714A] flex-shrink-0">
                {ICON_OPTIONS.map((ico) => (<option key={ico} value={ico}>{ico.replace('ri-', '').replace('-line', '')}</option>))}
              </select>
              <input type="text" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') editingId ? handleSaveEdit() : handleAdd(); }} placeholder="亮点标题" className="flex-1 px-3 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors" />
            </div>
            <input type="text" value={formDesc} onChange={(e) => setFormDesc(e.target.value)} placeholder="亮点描述" className="w-full px-3 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors" />
            <div className="flex gap-2 justify-end">
              <button onClick={resetForm} className="px-4 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#555] hover:bg-[#f7f5f2] transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={editingId ? handleSaveEdit : handleAdd} disabled={saving} className="px-4 py-2 rounded-lg bg-[#C8714A] text-white text-sm font-semibold hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">{editingId ? '保存修改' : '确认添加'}</button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-[#ede9e3] overflow-hidden">
        {loading ? (<div className="flex flex-col gap-3 p-4">{Array.from({ length: 4 }).map((_, i) => (<div key={i} className="h-14 bg-[#f7f5f2] rounded-lg animate-pulse" />))}</div>)
        : presets.length === 0 ? (<div className="flex flex-col items-center justify-center py-12 gap-3 text-[#aaa]"><div className="w-10 h-10 flex items-center justify-center bg-[#f7f5f2] rounded-full"><i className="ri-star-line text-lg"></i></div><p className="text-sm">暂无亮点预设，请添加</p></div>)
        : (<div>{presets.map((p, idx) => (
          <div key={p.id} className={`flex items-center gap-4 px-4 py-3 border-b border-[#f0ece6] last:border-0 ${idx % 2 === 0 ? '' : 'bg-[#fdfcfb]'}`}>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8714A]/10 flex-shrink-0"><i className={`${p.icon} text-[#C8714A] text-sm`}></i></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#1a1a1a]">{p.title}</div>
              <div className="text-xs text-[#999] truncate">{p.desc}</div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => handleStartEdit(p)} className="w-8 h-8 flex items-center justify-center rounded-lg text-[#888] hover:bg-[#f0ece6] hover:text-[#C8714A] transition-colors cursor-pointer" title="编辑"><i className="ri-edit-line text-sm"></i></button>
              <button onClick={() => handleDelete(p.id)} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg text-[#888] hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer" title="删除"><i className="ri-delete-bin-line text-sm"></i></button>
            </div>
          </div>
        ))}</div>)}
      </div>
    </div>
  );
}

// ===================== FAQs Panel =====================
function FaqsPanel({ showToast }: { showToast: (msg: string) => void }) {
  const [presets, setPresets] = useState<FaqPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const [formQ, setFormQ] = useState('');
  const [formA, setFormA] = useState('');

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from('faq_presets').select('*').order('sort_order', { ascending: true });
    if (!error && data) setPresets(data as FaqPreset[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchPresets(); }, [fetchPresets]);

  const resetForm = () => { setFormQ(''); setFormA(''); setEditingId(null); setAddOpen(false); };

  const handleAdd = async () => {
    if (!formQ.trim()) { showToast('请输入问题'); return; }
    setSaving(true);
    const maxSort = presets.reduce((max, p) => Math.max(max, p.sort_order), 0);
    const { error } = await supabase.from('faq_presets').insert({ question: formQ.trim(), answer: formA.trim(), sort_order: maxSort + 1 });
    setSaving(false);
    if (error) { showToast('添加失败'); return; }
    resetForm();
    showToast('常见问题已添加');
    fetchPresets();
  };

  const handleStartEdit = (p: FaqPreset) => {
    setEditingId(p.id); setFormQ(p.question); setFormA(p.answer); setAddOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !formQ.trim()) { showToast('请输入问题'); return; }
    setSaving(true);
    const { error } = await supabase.from('faq_presets').update({ question: formQ.trim(), answer: formA.trim() }).eq('id', editingId);
    setSaving(false);
    if (error) { showToast('修改失败'); return; }
    resetForm();
    showToast('常见问题已更新');
    fetchPresets();
  };

  const handleDelete = async (id: string) => {
    setSaving(true);
    const { error } = await supabase.from('faq_presets').delete().eq('id', id);
    setSaving(false);
    if (error) { showToast('删除失败'); return; }
    showToast('常见问题已删除');
    fetchPresets();
  };

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-[#888] mb-5">管理常见问题预设，这些内容会出现在新建/编辑商品弹窗的「常见问题」下拉框中。</p>

      <div className="mb-5">
        {!addOpen ? (
          <button onClick={() => { resetForm(); setAddOpen(true); }} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#C8714A] text-white text-sm font-semibold hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i>添加问题
          </button>
        ) : (
          <div className="rounded-xl border border-[#ede9e3] bg-white p-4 flex flex-col gap-3">
            <input type="text" value={formQ} onChange={(e) => setFormQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') editingId ? handleSaveEdit() : handleAdd(); }} placeholder="问题（如：猫薄荷的香味能持续多久？）" className="w-full px-3 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors" />
            <textarea value={formA} onChange={(e) => setFormA(e.target.value)} placeholder="回答内容" rows={3} className="w-full px-3 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#1a1a1a] placeholder-[#bbb] focus:outline-none focus:border-[#C8714A] transition-colors resize-none" />
            <div className="flex gap-2 justify-end">
              <button onClick={resetForm} className="px-4 py-2 rounded-lg border border-[#e8e4de] text-sm text-[#555] hover:bg-[#f7f5f2] transition-colors cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={editingId ? handleSaveEdit : handleAdd} disabled={saving} className="px-4 py-2 rounded-lg bg-[#C8714A] text-white text-sm font-semibold hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">{editingId ? '保存修改' : '确认添加'}</button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-[#ede9e3] overflow-hidden">
        {loading ? (<div className="flex flex-col gap-3 p-4">{Array.from({ length: 4 }).map((_, i) => (<div key={i} className="h-16 bg-[#f7f5f2] rounded-lg animate-pulse" />))}</div>)
        : presets.length === 0 ? (<div className="flex flex-col items-center justify-center py-12 gap-3 text-[#aaa]"><div className="w-10 h-10 flex items-center justify-center bg-[#f7f5f2] rounded-full"><i className="ri-question-line text-lg"></i></div><p className="text-sm">暂无常见问题预设，请添加</p></div>)
        : (<div>{presets.map((p, idx) => (
          <div key={p.id} className={`flex items-start gap-4 px-4 py-3 border-b border-[#f0ece6] last:border-0 ${idx % 2 === 0 ? '' : 'bg-[#fdfcfb]'}`}>
            <div className="w-6 h-6 flex items-center justify-center bg-[#C8714A]/10 rounded-full flex-shrink-0 mt-0.5"><i className="ri-question-line text-[#C8714A] text-xs"></i></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#1a1a1a]">{p.question}</div>
              <div className="text-xs text-[#999] mt-0.5 leading-relaxed line-clamp-2">{p.answer}</div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => handleStartEdit(p)} className="w-8 h-8 flex items-center justify-center rounded-lg text-[#888] hover:bg-[#f0ece6] hover:text-[#C8714A] transition-colors cursor-pointer" title="编辑"><i className="ri-edit-line text-sm"></i></button>
              <button onClick={() => handleDelete(p.id)} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg text-[#888] hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer" title="删除"><i className="ri-delete-bin-line text-sm"></i></button>
            </div>
          </div>
        ))}</div>)}
      </div>
    </div>
  );
}